import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import UserCoworkerQuery from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { userCoworkerApi } from "users/information/coworker-information/_api/user-coworker-api";
import {
  getErrorLoadingUserCoworkersByQuery,
  getIsLoadingUserCoworkersByQuery,
  getUserCoworkersByQuery,
} from "users/information/coworker-information/_stores/user-coworkers-redux-slice";

const PAGE_SIZE = 25;

type UseInfiniteUserCoworkersOptions = {
  skip?: boolean;
};

export default function useInfiniteUserCoworkers(
  query: UserCoworkerQuery,
  options?: UseInfiniteUserCoworkersOptions,
) {
  const skip = options?.skip ?? false;
  const dispatch = useAttorneyHubDispatch();
  const coworkers = useSelector(getUserCoworkersByQuery(query));
  const isLoading = useSelector(getIsLoadingUserCoworkersByQuery(query));
  const error = useSelector(getErrorLoadingUserCoworkersByQuery(query));
  const requestsRef = useRef(new Set<{ abort?: () => void; unsubscribe?: () => void }>());
  const queryRef = useRef(query);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const dispatchPage = useCallback(
    (pageIndex: number, append: boolean) => {
      const pageQuery = queryRef.current.withPagination(
        PaginationParameters.create({ pageIndex, pageSize: PAGE_SIZE }),
      );
      const request = dispatch(
        userCoworkerApi.endpoints.getUserCoworkers.initiate({
          query: pageQuery,
          append,
        }),
      );
      requestsRef.current.add(request);
      void request.finally(() => {
        requestsRef.current.delete(request);
        request.unsubscribe?.();
      });
      return request;
    },
    [dispatch],
  );

  useEffect(() => {
    if (skip) return;
    const request = dispatchPage(0, false);
    return () => {
      // A dialog can re-run this effect as it settles after opening. Do not cancel the
      // initial page in that cleanup: a replacement subscriber can use the same request.
      request.unsubscribe?.();
    };
  }, [dispatchPage, query.cacheKey, skip]);

  useEffect(
    () => () => {
      for (const request of requestsRef.current) {
        // Let RTK Query finish a short list request so a transient dialog unmount does not
        // turn the initial Coworkers load into an error that requires a manual retry.
        request.unsubscribe?.();
      }
      requestsRef.current.clear();
    },
    [],
  );

  const loadMore = useCallback(() => {
    if (skip || isLoading || !coworkers.hasNextPage) return;
    dispatchPage(coworkers.pageIndex + 1, true);
  }, [coworkers.hasNextPage, coworkers.pageIndex, dispatchPage, isLoading, skip]);

  const retry = useCallback(() => {
    if (skip || isLoading) return;
    const hasLoadedPage = coworkers.pageSize > 0;
    dispatchPage(hasLoadedPage ? coworkers.pageIndex + 1 : 0, hasLoadedPage);
  }, [coworkers.pageIndex, coworkers.pageSize, dispatchPage, isLoading, skip]);

  return useMemo(
    () => ({
      coworkers,
      isLoading,
      isInitialLoading: isLoading && coworkers.isEmpty,
      error,
      loadMore,
      retry,
    }),
    [coworkers, error, isLoading, loadMore, retry],
  );
}
